/**
 * QueuePrediction Component — Live wait times and best time to visit (refined)
 */
import React, { useState, useEffect } from 'react';
import { TrendingDown, AlertCircle } from 'lucide-react';
import { Spinner, ErrorMessage } from '../../shared';
import { queueApi } from '../../../services/api';

export function QueuePrediction({ restaurant, t }) {
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    let interval;

    async function fetchPrediction() {
      try {
        const data = await queueApi.getPrediction(restaurant.id);
        if (mounted) {
          setPrediction(data);
          setError(null);
        }
      } catch (err) {
        if (mounted) {
          if (restaurant.queueData) {
            setPrediction({
              currentWait: restaurant.queueData.currentWait || 0,
              bestTime: restaurant.queueData.bestTime || 'Now',
              hourlyPrediction: restaurant.queueData.history?.map((w, i) => ({
                hour: 10 + i * 2,
                waitMinutes: w,
                crowdLevel: w < 30 ? 'low' : w < 90 ? 'medium' : 'high'
              })) || [],
              confidence: 0.7,
              dataSource: 'estimated'
            });
          } else {
            setError(err);
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    fetchPrediction();
    interval = setInterval(fetchPrediction, 5 * 60 * 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [restaurant.id, restaurant.queueData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Spinner className="text-primary-500" />
      </div>
    );
  }

  if (error && !prediction) {
    return <ErrorMessage error={error} />;
  }

  return (
    <div className="bg-white border border-stone-100 rounded-2xl shadow-soft p-4">
      <h3 className="text-[11px] font-bold text-stone-700 mb-3 flex items-center justify-between uppercase tracking-wider">
        <span className="flex items-center">
          <span className="w-0.5 h-3.5 bg-blue-500 rounded-full mr-2" />
          Queue Prediction
        </span>
        <span className="text-[10px] bg-red-50 text-red-500 border border-red-100 px-2 py-0.5 rounded-full font-semibold animate-pulse-soft normal-case tracking-normal">
          {t ? t('live') : 'Live'}
        </span>
      </h3>

      {/* Current Stats */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wider">
            {t ? t('currentWait') : 'Current Wait'}
          </p>
          <p className="text-xl font-bold text-stone-800">
            {prediction?.currentWait || 0} <span className="text-sm font-medium text-stone-400">min</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-stone-400 font-medium uppercase tracking-wider">
            {t ? t('bestTime') : 'Best Time'}
          </p>
          <p className="text-sm font-semibold text-emerald-600 flex items-center justify-end gap-1">
            <TrendingDown size={13} />
            {prediction?.bestTime || 'Now'}
          </p>
        </div>
      </div>

      {/* Hourly Chart */}
      {prediction?.hourlyPrediction && prediction.hourlyPrediction.length > 0 && (
        <div className="h-14 flex items-end justify-between gap-0.5">
          {prediction.hourlyPrediction.map((h, i) => (
            <div
              key={i}
              className="w-full rounded-t relative group cursor-pointer"
              style={{ height: `${Math.min((h.waitMinutes / 240) * 100, 100)}%` }}
            >
              <div
                className="absolute bottom-0 w-full rounded-t bg-primary-500/30 hover:bg-primary-500/60 transition-all duration-200"
                style={{ height: '100%', borderRadius: '3px 3px 0 0' }}
              />
              <div className="opacity-0 group-hover:opacity-100 absolute -top-7 left-1/2 transform -translate-x-1/2 bg-stone-800 text-white text-[9px] px-1.5 py-0.5 rounded-md whitespace-nowrap font-medium transition-opacity duration-200">
                {h.hour}:00 — {h.waitMinutes}m
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Data Source Indicator */}
      {prediction?.dataSource === 'estimated' && (
        <p className="text-[10px] text-center text-stone-400 mt-2.5 flex items-center justify-center gap-1 font-medium">
          <AlertCircle size={10} />
          Estimated — AI prediction unavailable
        </p>
      )}
    </div>
  );
}

export default QueuePrediction;
